import { Injectable, inject } from '@angular/core';

import { NotificationVM } from '../models/notification.model';
import { NOTIFICATION_TYPE_LABELS } from '../models/notification-preference.model';
import { NotificationPreferencesService } from './notification-preferences.service';

/**
 * Shows arriving notifications on screen, subject to the user's per-type preferences.
 *
 * Fed by the header, which already refetches the list whenever the SignalR hub signals a change —
 * so this deliberately does no fetching of its own rather than doubling every push into two
 * requests. It only decides what, if anything, to surface.
 *
 * Uses the browser Notification API so an alert still lands when the tab is in the background,
 * which an in-app toast cannot do. This is the on-screen half only: reaching a phone with the app
 * closed needs web push and a service worker, which is a separate piece of work.
 */
@Injectable({ providedIn: 'root' })
export class NotificationPresenterService {
  private readonly preferences = inject(NotificationPreferencesService);

  /** Ids already considered. Seeded on the first call so history is never replayed as alerts. */
  private readonly seen = new Set<number>();
  private primed = false;

  get permission(): NotificationPermission | 'unsupported' {
    return this.isSupported ? Notification.permission : 'unsupported';
  }

  private get isSupported(): boolean {
    return typeof window !== 'undefined' && 'Notification' in window;
  }

  /** Asks the browser for permission. Must be called from a user gesture to work in most browsers. */
  async requestPermission(): Promise<NotificationPermission | 'unsupported'> {
    if (!this.isSupported) return 'unsupported';
    if (Notification.permission !== 'default') return Notification.permission;

    try {
      return await Notification.requestPermission();
    } catch {
      return Notification.permission;
    }
  }

  /**
   * Hands the presenter the latest list. Anything not seen before is a candidate to surface.
   *
   * The first call only records what is already there: opening the app would otherwise fire one
   * alert per unread notification in history.
   */
  present(notifications: NotificationVM[]): void {
    if (!this.primed) {
      notifications.forEach(n => this.seen.add(n.id));
      this.primed = true;
      return;
    }

    const arrived = notifications.filter(n => !this.seen.has(n.id));
    arrived.forEach(n => this.seen.add(n.id));

    if (arrived.length === 0) return;
    if (!this.isSupported || Notification.permission !== 'granted') return;

    // Newest last in the list order used by the header, so show oldest first and let the newest
    // land on top of the stack.
    arrived
      .filter(n => !n.isRead && this.preferences.showsOnScreen(n.type))
      .forEach(n => this.show(n));
  }

  /**
   * Subject line for the alert: the notification's own title, falling back to its category, and
   * suffixed with the company it concerns. A user works across several companies and an alert has no
   * surrounding screen to give it context, so "Leave Request" alone does not say whose.
   */
  private titleFor(note: NotificationVM): string {
    const base = note.title?.trim()
      || (note.type ? NOTIFICATION_TYPE_LABELS[note.type]?.title : undefined)
      || 'BizCombo';

    return note.companyName ? `${base} · ${note.companyName}` : base;
  }

  private show(note: NotificationVM): void {
    try {
      // tag = id so a duplicate push for the same notification replaces its alert instead of
      // stacking a second identical one.
      new Notification(this.titleFor(note), {
        body: note.message,
        tag: `bc-notification-${note.id}`,
        // Without an explicit icon the browser falls back to the page favicon, which showed the
        // default Angular mark rather than BizCombo's.
        icon: '/assets/cropped-bizcombo-1-192x192.png',
        badge: '/assets/cropped-bizcombo-1-32x32.png',
      });
    } catch {
      // Constructing a Notification throws on some platforms (notably Android Chrome, which
      // requires the service worker registration instead). Failing to alert must never break the
      // refresh that triggered it.
    }
  }
}
